const AUTH_PATHS = [
    '/sign-in',
    '/sign-up',
    '/reset-password',
    '/change-password',
] as const;

/**
 * Validate a post-login `return_to` value and return a safe SAME-ORIGIN path
 * (pathname + search + hash) to send the user back to after sign-in, or null if
 * it is unsafe.
 *
 * Because the value originates from a URL query param it is attacker-controllable,
 * so this is the client-side open-redirect guard: it rejects cross-origin,
 * protocol-relative, and scheme-bearing URLs (all resolve to a different
 * `origin`), and auth pages (which would loop the user back to sign-in). Kratos
 * independently enforces its `allowed_return_urls` allowlist as a second layer.
 */
export function sanitizeReturnToPath(
    value: string | null | undefined,
    origin: string,
): string | null {
    if (!value) {
        return null;
    }
    let url: URL;
    try {
        url = new URL(value, origin);
    } catch {
        return null;
    }
    if (url.origin !== origin) {
        return null;
    }
    // A same-origin URL can still have a `//`-prefixed path (e.g.
    // `https://app.phoebe.work//evil.com` → pathname `//evil.com`). Assigned to
    // `window.location`, `//evil.com` is read as a protocol-relative EXTERNAL
    // URL — reject it.
    if (url.pathname.startsWith('//')) {
        return null;
    }
    // Strip trailing slashes so `/sign-in/` is still caught as an auth page.
    const normalizedPathname = url.pathname.replace(/\/+$/u, '') || '/';
    if (AUTH_PATHS.some((authPath) => authPath === normalizedPathname)) {
        return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
}
